import React from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  PickersActionBar,
  PickersActionBarProps,
} from "@mui/x-date-pickers/PickersActionBar";
import { Box, Typography } from "@mui/material";
import { InputShell } from "components/FormInputShell";
import { formatLocalDateTime } from "utils/contentSchedule";

dayjs.extend(utc);

// MUI's adapter parses typed text in the browser's own timezone, which rejects a time inside that
// zone's DST gap even though it exists in UTC. This field is UTC-only, so typed text is parsed in UTC.
class AdapterDayjsUtc extends AdapterDayjs {
  parse = (value: string, format: string) =>
    value === "" ? null : dayjs.utc(value, format, true);
}

interface UtcDateTimeFieldProps {
  label: string;
  name: string;
  value?: string | null;
  minValue?: string | null;
  savedValue?: string | null;
  disabled?: boolean;
  onChange: (iso: string | null) => void;
}

const HOUR_MS = 60 * 60 * 1000;

// The floor is the next whole UTC second, or minValue when that is later.
const earliestAllowedMs = (minValue?: string | null) =>
  Math.max(
    Math.ceil(Date.now() / 1000) * 1000,
    minValue ? new Date(minValue).getTime() : 0,
  );

interface LocalTimeActionBarProps extends PickersActionBarProps {
  localTime?: string;
}

// MUI's own action bar, with the moment being picked shown in the admin's local time on its left.
const LocalTimeActionBar = ({
  localTime,
  className,
  ...props
}: LocalTimeActionBarProps) => (
  <Box
    className={className}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      pl: 2,
    }}
  >
    <Box>
      {localTime && (
        <>
          <Typography variant="caption" color="text.secondary" display="block">
            Your local time
          </Typography>
          <Typography variant="body2">{localTime}</Typography>
        </>
      )}
    </Box>
    <PickersActionBar {...props} />
  </Box>
);

// The picker itself runs in UTC (timezone="UTC"), so every UTC moment can be picked, including the
// hour a DST change skips in the admin's own zone.
const UtcDateTimeField = ({
  label,
  name,
  value,
  minValue,
  savedValue,
  disabled,
  onChange,
}: UtcDateTimeFieldProps) => {
  const minMs = earliestAllowedMs(minValue);
  const pickerValue = value ? dayjs.utc(value) : null;
  // The server keeps an unchanged saved date even once it has passed, so it is not shown as an error.
  const keepsSavedPastValue =
    !!value && value === savedValue && new Date(value).getTime() < minMs;

  const handleChange = (date: Dayjs | null) => {
    // A half-typed value is an invalid date; keeping the last valid value stops the field resetting mid-typing.
    if (date && !date.isValid()) return;
    const earliestMs = earliestAllowedMs(minValue);
    // Picking a day keeps the previous time (00:00:00 when empty), which is already past for today,
    // so it moves to the next full hour that is allowed.
    if (
      date &&
      date.valueOf() < earliestMs &&
      (!pickerValue || !date.isSame(pickerValue, "day"))
    ) {
      onChange(
        new Date(Math.ceil(earliestMs / HOUR_MS) * HOUR_MS).toISOString(),
      );
      return;
    }
    onChange(date ? date.toISOString() : null);
  };

  const actionBarProps: Partial<LocalTimeActionBarProps> = {
    actions: ["clear", "accept"],
    localTime: formatLocalDateTime(value),
  };

  return (
    <InputShell name={name} label={`${label} (UTC)`} removeBottomMargin>
      <LocalizationProvider dateAdapter={AdapterDayjsUtc}>
        <DateTimePicker
          timezone="UTC"
          value={pickerValue}
          referenceDate={dayjs.utc().startOf("day")}
          format="DD/MM/YYYY HH:mm:ss"
          views={["year", "month", "day", "hours", "minutes", "seconds"]}
          timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
          minDateTime={dayjs.utc(minMs)}
          ampm={false}
          disabled={disabled}
          onChange={handleChange}
          slots={{ actionBar: LocalTimeActionBar }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: keepsSavedPastValue ? false : undefined,
            },
            actionBar: actionBarProps,
          }}
        />
      </LocalizationProvider>
      {value && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          Your local time:{" "}
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            {formatLocalDateTime(value)}
          </Box>
        </Typography>
      )}
    </InputShell>
  );
};

export default UtcDateTimeField;
